/** Galvanized guard frame that continues the pen kerb upward. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

const RADIUS = 0.022;

function pipe(length: number, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(RADIUS, RADIUS, length, 8, 1), surface('metal', 0xaeb7b9));
  mesh.name = name;
  mesh.castShadow = true;
  return mesh;
}

/**
 * Build one rail span at a declared kerb interval.
 *
 * The default asset is a whole bay, but the gate bay has two shorter static
 * returns either side of its deliberate mouth. Keeping those as the same
 * factory prevents the visible rail from drifting away from the collision cut.
 */
export function penRailSpan(length = 2.4): THREE.Group {
  const group = new THREE.Group();
  group.name = 'pen-rail';
  // A boxed imitation of these return bends read as shelving at 64 px. The
  // actual circular returns preserve the agricultural-equipment read even when
  // the secondary rail is reduced to only a few pixels.
  for (const [y, name] of [[0.72, 'lower-rail'], [1.03, 'top-rail']] as const) {
    const horizontal = pipe(Math.max(0, length - 0.24), name);
    horizontal.rotation.x = Math.PI / 2;
    horizontal.position.y = y;
    group.add(horizontal);
  }
  for (const z of [-length / 2 + RADIUS, 0, length / 2 - RADIUS]) {
    // The uprights begin at the module's ground datum even though the two rails
    // only become visible above the 0.55 m kerb. That keeps this independently
    // placeable kit part honest to the universal origin contract; in a dressed
    // bay their lower length is simply occluded by its companion kerb.
    const upright = pipe(1.03, `upright-${z.toFixed(1)}`);
    upright.position.set(0, 0.515, z);
    group.add(upright);
  }
  for (const [z, mirror] of [[-length / 2 + 0.12 + RADIUS, 1], [length / 2 - 0.12 - RADIUS, -1]] as const) {
    const bend = new THREE.Mesh(
      new THREE.TorusGeometry(0.12, RADIUS, 6, 8, Math.PI / 2), surface('metal', 0xaeb7b9),
    );
    bend.name = mirror > 0 ? 'return-bend-back' : 'return-bend-front';
    bend.rotation.y = Math.PI / 2;
    bend.scale.z = mirror;
    bend.position.set(0, 0.91, z);
    bend.castShadow = true;
    group.add(bend);
  }
  return group;
}

export const penRailAsset: AssetDefinition = {
  id: 'pen-rail', label: 'Pen rail', description: 'Galvanized two-rail frame with round return bends, sized to sit above a kerb.',
  footprint: { width: 0.044, height: 1.052, length: 2.4 }, create: penRailSpan,
};
